import {
  Box,
  Button,
  Text,
  UnorderedList,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Alert,
} from "@chakra-ui/react";
import MembersField, { MemberOption } from "components/elements/MembersField";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { ContactRow } from "components/elements/ContactRow";
import { LoanContext } from "providers/loan";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { getErrorMessage } from "utils/errorHandler";
import { ContactModel } from "providers/loans/contact.model";

const LoanContacts = () => {
  const { user } = useTenant();
  const { loan, refetch: loadLoan } = useContext(LoanContext);

  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [contacts, setContacts] = useState<ContactModel[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [guarantor, setGuarantor] = useState(false);
  const [readLoan, setReadLoan] = useState(false);

  const readOnlyLoan = () => {
    if (isPermissionEnabled(user) && hasPermission(user, permissionConstants.readOnlyLoans)) {
      setReadLoan(true);
    }
  };

  useEffect(() => {
    readOnlyLoan();
  }, []);

  async function assignLoan() {
    try {
      if (members.length === 0) {
        onClose();
        return;
      }
      setLoading(true);
      await apiRequest("/supabase/loan_contacts/insert", {
        loan: loan!.id,
        users: members.map(member => member.value),
      });
      toast({
        title: "Loan Assigned",
        description: "The loan has been successfully assigned.",
      });
      loadLoan();
      onClose();
      setMembers([]);
    } catch (err) {
      toast({
        title: "Error Occured",
        description: getErrorMessage(err),
        status: "error",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function removeMember(id: string) {
    try {
      await apiRequest("/supabase/loan_contacts/delete", {
        id,
      });

      await loadLoan();
      toast({
        title: "Member Removed",
        description: "The team member was successfully removed from the loan.",
      });
    } catch (err) {
      toast({
        title: "Error Occurred",
        description: getErrorMessage(err),
        status: "error",
      });
      console.error(err);
    }
  }

  const canRemoveContact = (contact: ContactModel) => {
    // Can't remove yourself
    if (contact.user.id === user.id) return false;

    // Read-only users can't remove anyone
    if (readLoan) return false;

    // Admins can remove anyone
    if (user.role?.name === "Administrator") return true;

    // Users can remove contacts of their own type (lender/borrower)
    if (user.type === contact.user.type) return true;

    return false;
  };

  useEffect(() => {
    const contacts = (loan?.contacts ?? []).filter(contact => contact.user?.is_active);
    setContacts(contacts);
  }, [loan]);

  useEffect(() => {
    setGuarantor(
      contacts.find((contact: any) => contact.user?.role?.name === "Guarantor") ? true : false
    );
  }, [contacts]);

  return (
    <Box>
      <Button
        onClick={onOpen}
        isDisabled={readLoan}
        color="primary"
        variant="ghost"
        px="0px"
        size="sm"
      >
        Manage
      </Button>
      <UnorderedList listStyleType="none" p="0px" m="0px">
        {contacts.map((contact: any) => (
          <ContactRow
            key={contact.id}
            contact={contact}
            userType={user.type}
            editable={canRemoveContact(contact)}
            removeMember={removeMember}
          />
        ))}
      </UnorderedList>
      {user.type === "borrower" && user.id === loan?.borrower.id && !guarantor && (
        <Alert status="info" bg="#E2F6FF" mt="20px" fontSize="14px" borderRadius="5px">
          <FontAwesomeIcon color="#1B83AE" fontSize="16px" icon={["fal", "info-circle"]} />
          <Text ml="8px">You will be the guarantor on this loan until you add one.</Text>
        </Alert>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent borderRadius="20px">
          <ModalHeader pb="10px">Assign Contacts</ModalHeader>
          <ModalCloseButton />
          <ModalBody pt="0px">
            <Text fontSize="14px" lineHeight="24px" mb="12px">
              Assign the loan application to team members.
            </Text>
            <UnorderedList listStyleType="none" p="0px" m="0px">
              {contacts.map((contact: any) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  editable={canRemoveContact(contact)}
                  removeMember={removeMember}
                />
              ))}
            </UnorderedList>
            <Box mt="20px">
              <MembersField
                isMulti
                members={members}
                setMembers={setMembers}
                exclude={contacts.map((member: any) => member.user.id)}
                borrowerCompanyId={loan?.borrower_company?.id}
                types={["lender", "borrower"]}
              />
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button onClick={assignLoan} isLoading={loading} variant={getCustomButtonVariant(user)}>
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LoanContacts;
